import { useMemo } from 'react';
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useApi } from '../../../lib/api';
import { ApiProblemError, fetchJson } from '../../../lib/apiFetchJson';
import { useStreamInvalidation } from '../../../view-system/hooks/useStreamInvalidation';
import {
  RequestsResponseSchema,
  RequestDetailSchema,
  routeIdFromFeedId,
  type RequestsResponse,
  type RequestDetail,
  type RequestEntry,
  type RequestScope,
} from '@shared/flow/requests';

/**
 * Flow Requests feed — query key, fetch hook, SSE invalidation, and the
 * optimistic-prepend helper (U6, consumed here + by U7's fire-action flow).
 *
 * Query key root only (no trailing `q`) so callers can invalidate/prepend
 * across EVERY search variant at once (`invalidateQueries`/`setQueriesData`
 * default to a prefix match) — a fired action must show up whether or not
 * the rep currently has a search term typed into the tab.
 */
export const FLOW_REQUESTS_KEY = ['flow', 'requests'] as const;

/**
 * Statuses that mean "still in flight, something server-side could still
 * change this row" — the reconcile-on-read path (src/services/flow/
 * request-reconcile.ts) is the only thing that ever finalizes a `pending`/
 * `running` `ActionExecution`, and on localhost there is no push transport
 * (no SSE-triggering webhook callback reaches a local dev box), so nothing
 * re-runs that reconcile unless SOMETHING hits `GET /api/flow/requests`
 * again. `queued` is deliberately NOT included: it's a pre-send
 * `EnrollmentItem` row (not yet an `ActionExecution`), and reading the feed
 * again doesn't reconcile anything for it — polling for a merely-queued row
 * would be pure wasted traffic.
 */
const NON_TERMINAL_STATUSES: ReadonlySet<RequestEntry['status']> = new Set(['pending', 'running']);

const REQUESTS_POLL_INTERVAL_MS = 5000;

/**
 * B2 (QA feedback): completed runs were invisible until a manual refresh —
 * pure decision function behind `useFlowRequests`'s `refetchInterval`. Kept
 * standalone (not inlined into the `useQuery` call) so it's unit-testable
 * without mounting React Query's timer machinery for every case.
 */
export function computeRequestsRefetchInterval(entries: RequestsResponse | undefined): number | false {
  if (!entries || entries.length === 0) return false;
  return entries.some(entry => NON_TERMINAL_STATUSES.has(entry.status)) ? REQUESTS_POLL_INTERVAL_MS : false;
}

/**
 * GET /api/flow/requests?q=<search> (or ?contactId=<id>) — the merged
 * executions+enrollments feed (src/api/flow-requests.ts, U5). One query per
 * distinct (`q`, `contactId`) pair (empty string / undefined included),
 * staleTime Infinity per the project's cache-indefinitely +
 * invalidate-on-mutation rule — refreshed by `useInvalidateRequestsOnStream`
 * below, an explicit `invalidateQueries`/`setQueriesData` call (U7's
 * optimistic prepend), OR (B2) a short poll while the loaded feed still has
 * an in-flight row — see `computeRequestsRefetchInterval` above. The poll is
 * conditional, not a blanket interval: a feed with nothing in flight polls
 * exactly as often as before this fix (never), preserving the
 * cache-indefinitely default for the common case.
 *
 * `contactId` (item #7 review finding, flow-workflow-requests): an EXACT
 * match on the execution's contact id, used by "View all" (a contact's
 * Recent Actions section). Deliberately separate from `q` (a substring
 * match over the resolved display name/email) — seeding `q` with a contact's
 * NAME collides on substrings ("Jon" also matches "Jonathan") and matches
 * EVERYTHING for a nameless contact (empty seed string). When `contactId` is
 * passed, it takes over the request entirely and `q` is ignored — a caller
 * scoping by id has no reason to also substring-match by name.
 *
 * `scope` (U11, D4): `'mine'` (default) omits the `scope` param entirely —
 * the wire request is byte-identical to pre-U9 behavior, and the server's
 * own default is `mine` too (`RequestsQuerySchema`, src/api/flow-requests.ts)
 * so this is belt-and-suspenders, not load-bearing. `'all'` (the sidebar's
 * "Everyone" chip) is opt-in only and appends `scope=all`. Folded into the
 * query key so `mine`/`all` cache independently — see
 * `prependOptimisticRequest` below for why the optimistic prepend doesn't
 * need to know about scope at all to stay correct across both caches.
 */
export function useFlowRequests(q: string, contactId?: string, scope: RequestScope = 'mine') {
  const { apiFetch } = useApi();
  return useQuery<RequestsResponse>({
    queryKey: [...FLOW_REQUESTS_KEY, q, contactId ?? null, scope],
    staleTime: Infinity,
    refetchInterval: (query) => computeRequestsRefetchInterval(query.state.data),
    queryFn: () => {
      const params: string[] = [];
      if (contactId) params.push(`contactId=${encodeURIComponent(contactId)}`);
      else if (q.trim()) params.push(`q=${encodeURIComponent(q.trim())}`);
      if (scope !== 'mine') params.push(`scope=${scope}`);
      const qs = params.length ? `?${params.join('&')}` : '';
      return fetchJson(apiFetch, RequestsResponseSchema, `/api/flow/requests${qs}`);
    },
  });
}

/**
 * The already-fetched feed row for `feedId`, from ANY cached list variant
 * (`q`/`contactId`/`scope`) — `undefined` when no cached list holds it.
 *
 * Scans every list rather than reading the one unfiltered key because the
 * row a rep is opening may only exist in the `scope=all` list (someone
 * else's row) or in a `contactId`-scoped list ("View all"), and reading a
 * single hard-coded key would silently miss both.
 */
function findCachedRequestEntry(queryClient: QueryClient, feedId: string): RequestEntry | undefined {
  for (const [, cached] of queryClient.getQueriesData<RequestsResponse>({ queryKey: FLOW_REQUESTS_KEY })) {
    if (!Array.isArray(cached)) continue;
    const hit = cached.find(entry => entry.id === feedId);
    if (hit) return hit;
  }
  return undefined;
}

/**
 * `GET /api/flow/requests/:requestId` (U10/U12) — ONE fully-expanded row:
 * `{ entry, attempts, enrollment }`. Takes a FEED id (what
 * `selectedRequestId`/`openRequestDetail` carry) and maps it to the ROUTE id
 * the endpoint speaks (`routeIdFromFeedId`, U9) — the `ex-`/`ei-` spelling
 * exists only on the wire.
 *
 * Key `[...FLOW_REQUESTS_KEY, 'detail', routeId]` shares the feed's prefix ON
 * PURPOSE: `useInvalidateRequestsOnStream`'s prefix invalidate then refreshes
 * an OPEN detail on `execution_status_changed` too, for free. The cost of
 * that choice is that `prependOptimisticRequest` also walks this key — it
 * guards with an `Array.isArray` check (see there), which is what keeps a
 * detail object from being spread into a bogus array.
 */
export function useFlowRequestDetail(feedId: string | null) {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();
  const routeId = feedId === null ? null : routeIdFromFeedId(feedId);

  // First paint for the fire-and-open flow (U7): the row is already in a
  // cached LIST the moment the POST returns (`prependOptimisticRequest`), so
  // the header/inputs can render before the detail round-trip lands.
  // `placeholderData` (not `initialData`) on purpose — `initialData` would be
  // written into the cache and, under `staleTime: Infinity`, suppress the
  // real fetch entirely, permanently hiding `attempts`/`enrollment`.
  const cachedEntry = feedId === null ? undefined : findCachedRequestEntry(queryClient, feedId);
  const placeholderData = useMemo<RequestDetail | undefined>(
    () => (cachedEntry ? { entry: cachedEntry, attempts: [], enrollment: null } : undefined),
    [cachedEntry],
  );

  return useQuery<RequestDetail | null>({
    queryKey: [...FLOW_REQUESTS_KEY, 'detail', routeId],
    placeholderData,
    // A feed id with no route spelling (legacy/synthetic ids) has nothing to
    // ask the server about — `/api/flow/requests/null` is not a request worth
    // making. The component falls back to the cached list entry / not-found.
    enabled: routeId !== null,
    staleTime: Infinity,
    retry: false,
    // Same B2 self-heal the list does, off the SAME decision function — an
    // open detail on a `pending`/`running` row re-reads until it settles
    // (the read is what triggers server-side reconcile), then stops.
    refetchInterval: (query) =>
      computeRequestsRefetchInterval(query.state.data ? [query.state.data.entry] : undefined),
    queryFn: async () => {
      try {
        return await fetchJson(apiFetch, RequestDetailSchema, `/api/flow/requests/${routeId}`);
      } catch (err) {
        if (err instanceof ApiProblemError && err.kind === 'http' && err.status === 404) return null;
        throw err;
      }
    },
  });
}

/**
 * Invalidate every cached `['flow','requests', q]` query (any search term)
 * when the SSE stream reports `execution_status_changed` — the exact reason
 * the plan-review amendment settled on (U5 notes: emitted by both
 * `executeUnbound`'s call-sites and `reconcileUserRequests`'s lazy-finalize
 * path). Mirrors `useV4InvalidateOnStream`'s reason-gated invalidate
 * (useV4InvalidateOnStream.ts:340), but scoped to this feature's OWN query
 * family instead of the v4 `['executions']` key: the Requests feed is a
 * different read (merged execution+enrollment rows with contact display
 * names), not the v4 action-execution list that hook already refreshes.
 *
 * Called once from `EnrollQueueProvider` (mounted at shell level for the
 * lifetime of the app — see EnrollQueueContext.tsx file header) so the feed
 * stays live regardless of whether the Requests tab is currently open. A
 * reconnect also refetches, since the server does not replay missed frames.
 */
export function useInvalidateRequestsOnStream(): void {
  useStreamInvalidation({
    when: (event) => event.type === 'invalidate' && event.reason === 'execution_status_changed',
    keys: [FLOW_REQUESTS_KEY],
  });
}

/**
 * Whether `entry` would appear in a feed filtered by search term `q`. Mirrors
 * the server's `matchesQuery` (src/api/flow-requests.ts) exactly: empty `q`
 * matches everything; otherwise a case-insensitive substring match against
 * the entry's contact name OR email. Kept in lockstep with the server
 * predicate intentionally (not shared code) — see the file header on the
 * server side for why cache-side and server-side matching must agree: an
 * optimistic entry written into a cache the server would never have
 * returned for that `q` is exactly the bug this predicate closes.
 */
function matchesSearch(entry: RequestEntry, q: string): boolean {
  if (!q) return true;
  const needle = q.toLowerCase();
  const name = entry.contact?.name?.toLowerCase() ?? '';
  const email = entry.contact?.email?.toLowerCase() ?? '';
  return name.includes(needle) || email.includes(needle);
}

/**
 * Whether `entry` would appear in a feed filtered by (`q`, `contactId`).
 * `contactId` (item #7) takes over entirely when present — EXACT id match,
 * mirroring the server's `contactId` param — so a contactId-scoped cache
 * entry never gets an optimistic prepend for a DIFFERENT contact's fired
 * action just because its `q` component happens to be empty/match-all.
 */
function matchesFilter(entry: RequestEntry, q: string, contactId: string | null): boolean {
  if (contactId) return entry.contact?.id === contactId;
  return matchesSearch(entry, q);
}

/**
 * Optimistic prepend (for U7's fire-action flow): insert `entry` at the top
 * of every currently-cached Requests query whose search term `q` is empty or
 * matches the entry (see `matchesSearch` above) — ahead of the real row
 * landing on the next SSE-triggered refetch. A cache with no data yet (never
 * fetched), or one whose `q` doesn't match, is left alone: there is nothing
 * to prepend onto in the first case, and prepending into a non-matching
 * search view would transiently show a row the server would never return for
 * that `q` in the second (the SSE invalidate still refreshes it correctly
 * once the real fetch lands).
 *
 * U11 (D4): query keys now carry a 4th `scope` segment, but this function
 * deliberately doesn't read it — it only matches on `q`/`contactId`
 * (indexes `[2]`/`[3]`). The acting user's own newly-fired row belongs in
 * BOTH the `mine` and `all` cached lists for the same `q` (their own row is
 * a member of "everyone" too), so tolerating the extra key segment without
 * inspecting it is exactly the right behavior, not an oversight.
 */
export function prependOptimisticRequest(queryClient: QueryClient, entry: RequestEntry): void {
  for (const [queryKey, old] of queryClient.getQueriesData<RequestsResponse>({ queryKey: FLOW_REQUESTS_KEY })) {
    // U12: the prefix now also covers `['flow','requests','detail',routeId]`,
    // whose cached value is a `RequestDetail` OBJECT, not a feed array. Guard
    // on the shape — the `q`/`contactId` key-segment checks below happen to
    // skip it today only because a contact id never equals a route id, and
    // `[entry, ...old]` on an object throws the moment that coincidence ends.
    if (!Array.isArray(old)) continue;
    const q = typeof queryKey[2] === 'string' ? queryKey[2] : '';
    const contactId = typeof queryKey[3] === 'string' ? queryKey[3] : null;
    if (!matchesFilter(entry, q, contactId)) continue;
    queryClient.setQueryData<RequestsResponse>(queryKey, [entry, ...old]);
  }
}
